import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AgoraRTC from 'agora-rtc-sdk-ng';
import type { ILocalAudioTrack, ILocalVideoTrack } from 'agora-rtc-sdk-ng';
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  PhoneOff,
  Loader2,
  Clock,
  ShieldAlert,
  MonitorSmartphone,
  ArrowLeft,
} from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/Button';
import { liveClassService } from '../../services/live-class.service';
import {
  useLiveClassRoom,
  isSafariTooOld,
  type RemoteUser,
} from '../../hooks/useLiveClassRoom';
import { LiveRoomPanel } from '../../components/live-class-room/LiveRoomPanel';
import { useAuth } from '../../hooks/useAuth';
import type { LiveClass, LiveClassSession } from '@edytech/shared/types';

const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

function useCountdown(target: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (target === null) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);
  if (target === null) return 0;
  return Math.max(0, target - now);
}

function formatCountdown(ms: number): string {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

/**
 * Phase 2 — LiveClassRoom page (student + instructor). Server-authoritative
 * join-token (TRD §4.2/§6.6); lobby device test; Agora RTC room; token
 * refresh before expiry; graceful ended / blocked / device-denied states.
 * Dev stub mode (placeholders) simulates the room without a real Agora project.
 */
export const LiveClassRoom: React.FC = () => {
  const { classId = '', sessionId = '' } = useParams();
  const navigate = useNavigate();
  const sessionNum = Number(sessionId);

  const [liveClass, setLiveClass] = useState<LiveClass | null>(null);
  const [session, setSession] = useState<LiveClassSession | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [joinMuted, setJoinMuted] = useState(true);
  const [busy, setBusy] = useState(false);

  const getJoinToken = useCallback(() => liveClassService.getJoinToken(sessionNum), [sessionNum]);

  const handleSessionEnded = useCallback(() => {
    setLoadError('This session has ended.');
  }, []);

  const room = useLiveClassRoom({
    sessionId: sessionNum,
    getJoinToken,
    onSessionEnded: handleSessionEnded,
  });

  // Load class + session metadata for the lobby.
  useEffect(() => {
    if (!sessionNum || !classId) return;
    let cancelled = false;
    void (async () => {
      try {
        const detail = await liveClassService.getById(classId);
        if (cancelled) return;
        setLiveClass(detail);
        const found = detail.sessions?.find((s) => s.id === sessionNum) ?? null;
        setSession(found);
        if (!found) setLoadError('Session not found.');
      } catch (e) {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : 'Failed to load the session.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [classId, sessionNum]);

  const handleJoin = useCallback(
    (muted: boolean) => {
      setBusy(true);
      void room.joinRoom({ joinMuted: muted }).finally(() => setBusy(false));
    },
    [room]
  );


  // ---- Safari < 14.1 graceful block (TRD §6.6) ----
  if (isSafariTooOld()) {
    return (
      <div className="container mx-auto max-w-md px-4 py-16 text-center">
        <MonitorSmartphone className="mx-auto h-10 w-10 text-[rgb(var(--light-secondary))]" />
        <h1 className="mt-4 text-xl font-bold">Browser not supported</h1>
        <p className="mt-2 text-sm text-[rgb(var(--light-secondary))]">
          Live classes require Safari 14.1 or newer (or Chrome / Edge / Firefox). Please update
          your browser and try again.
        </p>
        <Button variant="secondary" className="mt-6" onClick={() => navigate(-1)}>
          Go back
        </Button>
      </div>
    );
  }

  if (room.phase === 'loading') {
    return (
      <div className="container mx-auto flex min-h-[50vh] items-center justify-center px-4">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (room.phase === 'ended') {
    return (
      <div className="container mx-auto max-w-md px-4 py-16 text-center">
        <ShieldAlert className="mx-auto h-10 w-10 text-[rgb(var(--light-secondary))]" />
        <h1 className="mt-4 text-xl font-bold">Session unavailable</h1>
        <p className="mt-2 text-sm text-[rgb(var(--light-secondary))]">
          {loadError || 'This session has ended or was cancelled.'}
        </p>
        <Link
          to={`/live-classes/${classId}`}
          className="mt-6 inline-flex items-center gap-1 text-sm text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> Back to live class
        </Link>
      </div>
    );
  }

  if (room.phase === 'left') {
    return (
      <div className="container mx-auto max-w-md px-4 py-16 text-center">
        <PhoneOff className="mx-auto h-10 w-10 text-[rgb(var(--light-secondary))]" />
        <h1 className="mt-4 text-xl font-bold">You left the room</h1>
        <p className="mt-2 text-sm text-[rgb(var(--light-secondary))]">You can rejoin while the session is open.</p>
        <div className="mt-6 flex justify-center gap-3">
          <Button onClick={() => handleJoin(joinMuted)}>Rejoin</Button>
          <Button variant="secondary" onClick={() => navigate(`/live-classes/${classId}`)}>
            Go to live class
          </Button>
        </div>
      </div>
    );
  }

  if (room.phase === 'inRoom') {
    return (
      <InRoomView
        room={room}
        session={session}
        liveClass={liveClass}
        onLeave={room.leaveRoom}
        onHome={() => navigate(`/live-classes/${classId}`)}
      />
    );
  }

  return (
    <LobbyView
      room={room}
      session={session}
      liveClass={liveClass}
      loadError={loadError}
      joinMuted={joinMuted}
      setJoinMuted={setJoinMuted}
      busy={busy}
      onJoin={() => handleJoin(joinMuted)}
      onBack={() => navigate(-1)}
    />
  );
};

/* ============================ Sub-views ============================ */

const DevicePreview: React.FC<{ enabled: boolean }> = ({ enabled }) => {
  const videoWrapRef = useRef<HTMLDivElement | null>(null);
  const tracksRef = useRef<{ audio: ILocalAudioTrack; video: ILocalVideoTrack } | null>(null);
  const [state, setState] = useState<'idle' | 'starting' | 'previewing' | 'denied'>('idle');

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void (async () => {
      try {
        const [audio, video] = await AgoraRTC.createMicrophoneAndCameraTracks();
        if (cancelled) {
          audio.close();
          video.close();
          return;
        }
        tracksRef.current = { audio, video };
        if (videoWrapRef.current) video.play(videoWrapRef.current);
        setState('previewing');
      } catch {
        if (!cancelled) setState('denied');
      }
    })();
    return () => {
      cancelled = true;
      void tracksRef.current?.audio.close();
      void tracksRef.current?.video.close();
      tracksRef.current = null;
    };
  }, [enabled]);

  return (
    <div>
      <div
        ref={videoWrapRef}
        className="aspect-video w-full overflow-hidden rounded-xl border border-[rgb(var(--border))] bg-black"
      >
        {state !== 'previewing' && (
          <div className="flex h-full items-center justify-center text-xs text-white/50">
            {enabled ? 'Starting camera…' : 'No camera preview'}
          </div>
        )}
      </div>
      {state === 'denied' && (
        <p className="mt-2 text-xs text-red-600">
          Camera/microphone access was blocked. You can still join muted.
        </p>
      )}
    </div>
  );
};

interface RoomViewProps {
  room: ReturnType<typeof useLiveClassRoom>;
  session: LiveClassSession | null;
  liveClass: LiveClass | null;
}

interface LobbyViewProps extends RoomViewProps {
  loadError: string | null;
  joinMuted: boolean;
  setJoinMuted: (v: boolean) => void;
  busy: boolean;
  onJoin: () => void;
  onBack: () => void;
}

const LobbyView: React.FC<LobbyViewProps> = ({
  room,
  session,
  liveClass,
  loadError,
  joinMuted,
  setJoinMuted,
  busy,
  onJoin,
  onBack,
}) => {
  const startsAt = session ? new Date(session.startsAt).getTime() : null;
  const countdown = useCountdown(startsAt);
  const [previewEnabled, setPreviewEnabled] = useState(false);

  const statusLabel: Record<string, string> = {
    SCHEDULED: 'Scheduled',
    ONGOING: 'Live now',
    ENDED: 'Ended',
    CANCELLED: 'Cancelled',
  };

  return (
    <div className="container mx-auto max-w-3xl px-4 py-10">
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-1 text-sm text-[rgb(var(--light-secondary))] hover:text-primary">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {room.error && (
        <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700">
          {room.error.message}
        </div>
      )}
      {loadError && (
        <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-700">
          {loadError}
        </div>
      )}

      <Card className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">{session?.title || liveClass?.title || 'Live Session'}</h1>
            <p className="mt-1 flex items-center gap-2 text-sm text-[rgb(var(--light-secondary))]">
              <Clock className="h-4 w-4" /> {session ? formatWhen(session.startsAt) : '—'}
            </p>
            {session && (
              <p className="mt-1 text-xs text-[rgb(var(--light-secondary))]">
                Ends {formatWhen(session.endsAt)} ({session.timezone})
              </p>
            )}
          </div>
          <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
            {session ? (statusLabel[session.status] ?? session.status) : '—'}
          </span>
        </div>

        {session && session.status === 'SCHEDULED' && countdown > 0 && (
          <div className="mt-4 rounded-lg bg-[rgb(var(--border))]/50 p-3 text-center">
            <p className="text-xs uppercase tracking-wide text-[rgb(var(--light-secondary))]">Starts in</p>
            <p className="font-mono text-2xl font-bold">{formatCountdown(countdown)}</p>
          </div>
        )}

        {session && session.status === 'ONGOING' && (
          <div className="mt-4 rounded-lg bg-green-500/10 p-3 text-center text-sm font-medium text-green-700">
            This session is live now
          </div>
        )}

        {/* Recording consent banner (TRD §6.6 consent/privacy) */}
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-[rgb(var(--border))] p-3 text-xs text-[rgb(var(--light-secondary))]">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            <strong className="text-[rgb(var(--foreground))]">This session is recorded.</strong>{' '}
            By joining you consent to being recorded. Recordings are published only by administrators.
          </p>
        </div>

        {/* Dev stub notice */}
        {room.stub && (
          <div className="mt-4 rounded-lg border border-dashed border-[rgb(var(--border))] p-3 text-xs text-[rgb(var(--light-secondary))]">
            <strong>Dev stub mode:</strong> Agora keys are not configured, so the room is simulated
            locally (no real media stream).
          </div>
        )}

        {/* Device test */}
        <div className="mt-5">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold">Device test</h3>
            <button
              onClick={() => setPreviewEnabled((v) => !v)}
              className="text-xs font-medium text-primary hover:underline"
            >
              {previewEnabled ? 'Stop preview' : 'Test mic & camera'}
            </button>
          </div>
          <DevicePreview enabled={previewEnabled} />
        </div>

        {room.deviceError && (
          <p className="mt-3 text-xs text-red-600">{room.deviceError}</p>
        )}

        <label className="mt-4 flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={joinMuted}
            onChange={(e) => setJoinMuted(e.target.checked)}
            className="h-4 w-4 accent-[rgb(var(--primary))]"
          />
          Join muted (mic off, camera on)
        </label>

        <Button className="mt-5 w-full" disabled={busy || room.phase !== 'lobby'} onClick={onJoin}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Video className="mr-2 h-4 w-4" />}
          {room.stub ? 'Join (stub mode)' : 'Join live class'}
        </Button>
      </Card>
    </div>
  );
};


interface InRoomViewProps extends RoomViewProps {
  onLeave: () => void;
  onHome: () => void;
}

const InRoomView: React.FC<InRoomViewProps> = ({ room, session, liveClass, onLeave, onHome }) => {
  const localWrapRef = useRef<HTMLDivElement | null>(null);
  const { user } = useAuth();
  const sessionId = session?.id ?? 0;
  const canModerate = room.isHost || user?.role === 'admin';

  useEffect(() => {
    if (room.localVideoTrack && localWrapRef.current) {
      room.localVideoTrack.play(localWrapRef.current);
    }
  }, [room.localVideoTrack]);

  const grid = (users: RemoteUser[]) => (
    <div className="grid flex-1 grid-cols-1 gap-3 overflow-y-auto p-4 md:grid-cols-2">
      {/* Local */}
      <div className="relative aspect-video overflow-hidden rounded-xl bg-slate-900">
        <div ref={localWrapRef} className="h-full w-full" />
        {room.cameraOff && (
          <div className="absolute inset-0 flex items-center justify-center text-4xl">🙂</div>
        )}
        <span className="absolute bottom-2 left-2 rounded bg-black/60 px-2 py-0.5 text-xs">
          You {room.micMuted && <MicOff className="ml-1 inline h-3 w-3" />}
        </span>
      </div>
      {/* Remote */}
      {users.map((u) => (
        <div key={u.uid} className="relative aspect-video overflow-hidden rounded-xl bg-slate-900">
          <div id={`remote-video-${u.uid}`} className="h-full w-full" />
          {!u.hasVideo && (
            <div className="absolute inset-0 flex items-center justify-center text-4xl">🧑‍🏫</div>
          )}
          <span className="absolute bottom-2 left-2 rounded bg-black/60 px-2 py-0.5 text-xs">
            {u.displayName} {!u.hasAudio && <MicOff className="ml-1 inline h-3 w-3" />}
          </span>
        </div>
      ))}
      {users.length === 0 && (
        <div className="flex items-center justify-center rounded-xl border border-dashed border-slate-700 p-8 text-sm text-slate-400">
          Waiting for other participants…
        </div>
      )}
    </div>
  );

  return (
    <div className="flex h-screen flex-col bg-slate-950 text-white">
      <header className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
        <div className="min-w-0">
          <h1 className="truncate text-sm font-semibold">
            {session?.title || liveClass?.title || 'Live Session'}
          </h1>
          <p className="text-xs text-slate-400">
            {room.isHost ? 'Host' : 'Participant'} ·{' '}
            {room.token?.mode === 'broadcast' ? 'Broadcast mode' : 'Interactive mode'}
            {room.stub && ' · stub'}
          </p>
        </div>
        <button onClick={onHome} className="text-xs text-slate-400 hover:text-white">
          Leave page
        </button>
      </header>

      <div className="flex min-h-0 flex-1">
        {grid(room.remoteUsers)}
        {sessionId > 0 && (
          <LiveRoomPanel
            sessionId={sessionId}
            canModerate={canModerate}
            currentUserId={user?.id ? Number(user.id) : undefined}
          />
        )}
      </div>

      <footer className="flex items-center justify-center gap-3 border-t border-slate-800 px-4 py-4">
        <button
          onClick={() => void room.toggleMic()}
          className={`flex h-12 w-12 items-center justify-center rounded-full ${
            room.micMuted ? 'bg-red-600 text-white' : 'bg-slate-700 text-white hover:bg-slate-600'
          }`}
          title={room.micMuted ? 'Unmute mic' : 'Mute mic'}
        >
          {room.micMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
        </button>
        <button
          onClick={() => void room.toggleCamera()}
          className={`flex h-12 w-12 items-center justify-center rounded-full ${
            room.cameraOff ? 'bg-red-600 text-white' : 'bg-slate-700 text-white hover:bg-slate-600'
          }`}
          title={room.cameraOff ? 'Turn camera on' : 'Turn camera off'}
        >
          {room.cameraOff ? <VideoOff className="h-5 w-5" /> : <Video className="h-5 w-5" />}
        </button>
        <button
          onClick={() => void onLeave()}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-500"
          title="Leave"
        >
          <PhoneOff className="h-5 w-5" />
        </button>
      </footer>
    </div>
  );
};

